// API utility functions with Basic Auth support

import { LogsResponse, OverviewData, PropertiesResponse } from "@gentrometer/shared";
import _ from 'lodash';

interface Credentials {
  username: string;
  password: string;
}

interface ApiCallOptions extends RequestInit {
  headers?: Record<string, string>;
}

// API base URL
// In development: React dev server calls Express on port 5000
// In production: Express serves both React app and API at the site root.
// Use window location to determine environment
const API_BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000/api' 
  : '/api';

// Store credentials in sessionStorage (cleared when browser closes)
export const setCredentials = (username: string, password: string): void => {
  sessionStorage.setItem('admin_username', username);
  sessionStorage.setItem('admin_password', password);
};

export const clearCredentials = (): void => {
  sessionStorage.removeItem('admin_username');
  sessionStorage.removeItem('admin_password');
};

export const getCredentials = (): Credentials | null => {
  const username = sessionStorage.getItem('admin_username');
  const password = sessionStorage.getItem('admin_password');
  return username && password ? { username, password } : null;
};

// Create Basic Auth header
const createAuthHeader = (): Record<string, string> => {
  const creds = getCredentials();
  if (!creds) return {};
  
  const token = btoa(`${creds.username}:${creds.password}`);
  return {
    'Authorization': `Basic ${token}`
  };
};

// Make authenticated API call
export const apiCall = async <T = unknown>(endpoint: string, options: ApiCallOptions = {}): Promise<T> => {
  const url = `${API_BASE}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...createAuthHeader(),
    ...options.headers
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 401) {
      clearCredentials();
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

async function _genOverviewData(): Promise<OverviewData> {
  return await apiCall<OverviewData>('/overview');
}
export const genOverviewData = _.memoize(_genOverviewData);

async function _genLogs(page: number): Promise<LogsResponse> {
  return await apiCall<LogsResponse>(`/logs?page=${page}`);
}

// Use resolver to include page number in cache key
export const genLogs = _.memoize(_genLogs, (page: number) => page);

async function _genProperties(page: number, status?: 'for_sale' | 'sold' | 'both', search?: string): Promise<PropertiesResponse> {
  const statusParam = status && status !== 'both' ? `&status=${status}` : '';
  const searchParam = search && search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
  return await apiCall<PropertiesResponse>(`/properties?page=${page}${statusParam}${searchParam}`);
}

// Use resolver to include page number, status, and search in cache key
export const genProperties = _.memoize(_genProperties, (page: number, status?: 'for_sale' | 'sold' | 'both', search?: string) => `${page}-${status || 'both'}-${search || ''}`);

/**
 * Download properties as CSV
 * 
 * @param status - Optional status filter ('for_sale', 'sold', or 'both')
 * @param search - Optional search text filter
 * @returns Promise that resolves with the CSV blob
 */
export const downloadPropertiesCSV = async (status?: 'for_sale' | 'sold' | 'both', search?: string): Promise<Blob> => {
  // Build query parameters
  const params = new URLSearchParams();
  if (status && status !== 'both') {
    params.append('status', status);
  }
  if (search) {
    params.append('search', search);
  }
  
  const queryString = params.toString();
  const endpoint = `/properties/csv${queryString ? `?${queryString}` : ''}`;
  
  // Use apiCall but handle blob response
  const url = `${API_BASE}${endpoint}`;
  const headers = {
    ...createAuthHeader()
  };

  const response = await fetch(url, {
    headers
  });

  if (response.status === 401) {
    clearCredentials();
    throw new Error('Authentication failed');
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.blob();
};
